import { useState, useEffect } from 'react';
import { Save, Layout, RefreshCw, Upload, Image as ImageIcon, X } from 'lucide-react';
import { adminApi, homeApi } from '@/utils/api';
import { useNotify } from '@/context/NotificationContext';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useStore } from '@/store/useStore';

export const UISettingsManager = () => {
    const [logoText, setLogoText] = useState('');
    const [tileBgs, setTileBgs] = useState<any>({});
    const { fetchTileBgs } = useStore();
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { showNotification } = useNotify();
    const { impactOccurred } = useHapticFeedback();

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            homeApi.getLogoText().then(res => res.json()),
            homeApi.getProfileTileBgs().then(res => res.json())
        ]).then(([logoData, tileData]) => {
            if (logoData.success) setLogoText(logoData.text || '');
            if (tileData.success) setTileBgs(tileData.backgrounds || {});
        }).catch(err => {
            console.error("Failed to load UI settings:", err);
            showNotification('Failed to load UI settings', 'error');
        }).finally(() => setIsLoading(false));
    }, []);

    const handleSave = async () => {
        impactOccurred('medium');
        setIsSaving(true);
        try {
            const [logoRes, tileRes] = await Promise.all([
                adminApi.setLogoText(logoText),
                adminApi.setProfileTileBgs(tileBgs)
            ]);
            
            const logoData = await logoRes.json();
            const tileData = await tileRes.json();
            
            if (logoData.success && tileData.success) {
                showNotification('UI settings updated successfully', 'success');
            } else {
                showNotification('Some settings failed to update', 'warning');
            }
        } catch (err) {
            console.error("Save UI settings error:", err);
            showNotification('Error saving UI settings', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleFileUpload = async (key: string, file: File) => {
        setIsUploading(key);
        console.log(`Starting upload for ${key}:`, file.name, `${(file.size / 1024 / 1024).toFixed(2)} MB`);
        try {
            const res = await adminApi.uploadUIAsset(file);
            const data = await res.json();
            if (data.success && data.url) {
                const newBgs = { ...tileBgs, [key]: data.url };
                setTileBgs(newBgs);
                
                // Save to DB
                await adminApi.setProfileTileBgs(newBgs);
                
                // Force-refresh global store so Menu/Wallet tiles update immediately
                await fetchTileBgs(true);
                
                showNotification(`${key.replace('bg_', '').replace(/_/g, ' ')} background saved!`, 'success');
                impactOccurred('medium');
            } else {
                console.error("Upload failed with data:", data);
                showNotification(data.error || 'Upload failed', 'error');
            }
        } catch (err: any) {
            console.error("Upload exception catch:", err);
            showNotification(err.message || 'Upload error', 'error');
        } finally {
            setIsUploading(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-12">
                <RefreshCw className="animate-spin text-white/20" size={32} />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-[#1c1c1e] rounded-2xl p-6 border border-white/5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-bl-full blur-3xl -z-10" />
                
                <h2 className="text-xl font-black text-white mb-2 flex items-center gap-2">
                    <Layout className="text-blue-500" />
                    UI Settings Tracker
                </h2>
                <p className="text-sm text-white/50 mb-6">
                    Modify global user interface configurations actively displayed across the application.
                </p>

                <div className="space-y-4">
                    <div className="bg-black/20 p-4 rounded-xl border border-white/5">
                        <label className="text-xs font-bold text-white/60 uppercase tracking-wider mb-2 block">
                            Animated Logo Text
                        </label>
                        <p className="text-[10px] text-white/40 mb-3">
                            This text will be animated using masks and gradients next to the Kite logo in the header. Leave blank to disable.
                        </p>
                        <input
                            type="text"
                            value={logoText}
                            onChange={(e) => setLogoText(e.target.value)}
                            placeholder="e.g. Generating..."
                            className="w-full bg-[#2c2c2e] text-white rounded-xl px-4 py-3 border border-white/10 focus:outline-none focus:border-blue-500 transition-colors placeholder:text-white/20"
                        />
                    </div>

                    <div className="bg-black/20 p-4 rounded-xl border border-white/5 space-y-4">
                        <label className="text-xs font-bold text-white/60 uppercase tracking-wider block">
                            Profile Tile Backgrounds (Photo Upload)
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { key: 'bg_friends', label: 'Friends Tile' },
                                { key: 'bg_level', label: 'Level Progress Tile' },
                                { key: 'bg_collection', label: 'My Collection (Full)' },
                                { key: 'bg_skins', label: 'Covers (Skins)' },
                                { key: 'bg_liked', label: 'Favorites' },
                                { key: 'bg_portfolio', label: 'Portfolio (Brands)' },
                                { key: 'bg_listings', label: 'My Listings' },
                                { key: 'bg_bids', label: 'My Bids' },
                            ].map(tile => {
                                const bgUrl = tileBgs[tile.key];
                                const isVideo = bgUrl?.match(/\.(mp4|webm|mov)$/i);
                                return (
                                <div key={tile.key} className="bg-[#2c2c2e] p-3 rounded-2xl border border-white/5 flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-black/40 border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center relative group">
                                        {bgUrl ? (
                                            <>
                                                {isVideo ? (
                                                    <video src={bgUrl} autoPlay loop muted playsInline className="w-full h-full object-cover" />
                                                ) : (
                                                    <img src={bgUrl} alt="preview" className="w-full h-full object-cover" />
                                                )}
                                                <button 
                                                    onClick={() => setTileBgs((prev: any) => ({ ...prev, [tile.key]: '' }))}
                                                    className="absolute inset-0 bg-red-500/80 items-center justify-center hidden group-hover:flex transition-all"
                                                >
                                                    <X size={16} className="text-white" />
                                                </button>
                                            </>
                                        ) : (
                                            <ImageIcon className="text-white/20" size={20} />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <label className="text-[10px] text-white/40 uppercase mb-1 block">{tile.label}</label>
                                        <div className="flex items-center gap-2">
                                            <label className="flex-1">
                                                <div className={`cursor-pointer bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-[10px] font-bold text-white transition-all flex items-center justify-center gap-2 ${isUploading === tile.key ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    {isUploading === tile.key ? (
                                                        <RefreshCw size={12} className="animate-spin" />
                                                    ) : (
                                                        <Upload size={12} />
                                                    )}
                                                    {tileBgs[tile.key] ? 'Change Photo' : 'Upload Photo'}
                                                </div>
                                                <input
                                                    type="file"
                                                    accept="image/*,video/*,.gif,.mp4,.webm,.mov,.heic,.heif,.jpg,.jpeg,.png"
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleFileUpload(tile.key, file);
                                                    }}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-black/20 p-4 rounded-xl border border-white/5 space-y-4">
                        <label className="text-xs font-bold text-white/60 uppercase tracking-wider block">
                            Menu Tile Backgrounds (Photo Upload)
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { key: 'bg_menu_wallet', label: 'Wallet' },
                                { key: 'bg_menu_add_username', label: 'Add Username' },
                                { key: 'bg_menu_my_items', label: 'My Items' },
                                { key: 'bg_menu_profile', label: 'Profile' },
                                { key: 'bg_menu_store', label: 'Store' },
                                { key: 'bg_menu_auctions', label: 'Auctions' },
                                { key: 'bg_menu_levels', label: 'Premium Levels' },
                                { key: 'bg_menu_covers', label: 'Profile Covers' },
                                { key: 'bg_menu_wallet_cards', label: 'Wallet Cards' },
                                { key: 'bg_menu_ranking', label: 'Elite Ranking' },
                                { key: 'bg_menu_usernames', label: 'Usernames' },
                                { key: 'bg_menu_settings', label: 'Settings' },
                                { key: 'bg_menu_about', label: 'About' },
                                { key: 'bg_menu_terms', label: 'Terms & Rules' },
                            ].map(tile => {
                                const bgUrl = tileBgs[tile.key];
                                const isVideo = bgUrl?.match(/\.(mp4|webm|mov)$/i);
                                return (
                                <div key={tile.key} className="bg-[#2c2c2e] p-3 rounded-2xl border border-white/5 flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-black/40 border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center relative group">
                                        {bgUrl ? (
                                            <>
                                                {isVideo ? (
                                                    <video src={bgUrl} autoPlay loop muted playsInline className="w-full h-full object-cover" />
                                                ) : (
                                                    <img src={bgUrl} alt="preview" className="w-full h-full object-cover" />
                                                )}
                                                <button 
                                                    onClick={() => {
                                                        const newBgs = { ...tileBgs, [tile.key]: '' };
                                                        setTileBgs(newBgs);
                                                        adminApi.setProfileTileBgs(newBgs);
                                                    }}
                                                    className="absolute inset-0 bg-red-500/80 items-center justify-center hidden group-hover:flex transition-all"
                                                >
                                                    <X size={16} className="text-white" />
                                                </button>
                                            </>
                                        ) : (
                                            <ImageIcon className="text-white/20" size={20} />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <label className="text-[10px] text-white/40 uppercase mb-1 block">{tile.label}</label>
                                        <div className="flex items-center gap-2">
                                            <label className="flex-1">
                                                <div className={`cursor-pointer bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-[10px] font-bold text-white transition-all flex items-center justify-center gap-2 ${isUploading === tile.key ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    {isUploading === tile.key ? (
                                                        <RefreshCw size={12} className="animate-spin" />
                                                    ) : (
                                                        <Upload size={12} />
                                                    )}
                                                    {tileBgs[tile.key] ? 'Change' : 'Upload'}
                                                </div>
                                                <input
                                                    type="file"
                                                    accept="image/*,video/*,.gif,.mp4,.webm,.mov,.heic,.heif,.jpg,.jpeg,.png"
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleFileUpload(tile.key, file);
                                                    }}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>

                    <div className="bg-black/20 p-4 rounded-xl border border-white/5 space-y-4">
                        <label className="text-xs font-bold text-white/60 uppercase tracking-wider block">
                            Wallet Stats Card Backgrounds (Photo Upload)
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { key: 'bg_wallet_stat_deposited', label: 'Deposited Card' },
                                { key: 'bg_wallet_stat_withdrawn', label: 'Withdrawn Card' },
                                { key: 'bg_wallet_stat_purchased', label: 'Purchased Card' },
                                { key: 'bg_wallet_stat_earned', label: 'Earned Card' },
                                { key: 'bg_wallet_stat_frozen', label: 'Frozen Card' },
                                { key: 'bg_wallet_stat_discount', label: 'Discount Card' },
                            ].map(tile => {
                                const bgUrl = tileBgs[tile.key];
                                const isVideo = bgUrl?.match(/\.(mp4|webm|mov)$/i);
                                return (
                                <div key={tile.key} className="bg-[#2c2c2e] p-3 rounded-2xl border border-white/5 flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-black/40 border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center relative group">
                                        {bgUrl ? (
                                            <>
                                                {isVideo ? (
                                                    <video src={bgUrl} autoPlay loop muted playsInline className="w-full h-full object-cover" />
                                                ) : (
                                                    <img src={bgUrl} alt="preview" className="w-full h-full object-cover" />
                                                )}
                                                <button 
                                                    onClick={() => {
                                                        const newBgs = { ...tileBgs, [tile.key]: '' };
                                                        setTileBgs(newBgs);
                                                        adminApi.setProfileTileBgs(newBgs);
                                                    }}
                                                    className="absolute inset-0 bg-red-500/80 items-center justify-center hidden group-hover:flex transition-all"
                                                >
                                                    <X size={16} className="text-white" />
                                                </button>
                                            </>
                                        ) : (
                                            <ImageIcon className="text-white/20" size={20} />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <label className="text-[10px] text-white/40 uppercase mb-1 block">{tile.label}</label>
                                        <div className="flex items-center gap-2">
                                            <label className="flex-1">
                                                <div className={`cursor-pointer bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-[10px] font-bold text-white transition-all flex items-center justify-center gap-2 ${isUploading === tile.key ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    {isUploading === tile.key ? (
                                                        <RefreshCw size={12} className="animate-spin" />
                                                    ) : (
                                                        <Upload size={12} />
                                                    )}
                                                    {tileBgs[tile.key] ? 'Change' : 'Upload'}
                                                </div>
                                                <input
                                                    type="file"
                                                    accept="image/*,video/*,.gif,.mp4,.webm,.mov,.heic,.heif,.jpg,.jpeg,.png"
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleFileUpload(tile.key, file);
                                                    }}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                    </div>
                </div>

                <div className="mt-8">
                    <button
                        disabled={isSaving}
                        onClick={handleSave}
                        className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${isSaving ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/20 active:scale-[0.98]'}`}
                    >
                        {isSaving ? (
                            <RefreshCw size={20} className="animate-spin" />
                        ) : (
                            <Save size={20} />
                        )}
                        {isSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>
        </div>
    );
};
